import { useRef } from 'react'
import clsx from 'clsx'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { Checkbox } from '@/ui/Checkbox'

import styles from './RouteSelector.module.scss'
import { routeSorter } from '@/pages/Dashboard/helpers/sortRoutes'

interface RouteSelectorProps {
    selectedRoutes: string[]
    availableRoutes: string[]
    searchValue: string
    onChange: (routes: string[]) => void
    triggerComponent?: React.ReactNode
}

export const RouteSelector = ({
    availableRoutes,
    selectedRoutes,
    searchValue,
    onChange,
    triggerComponent,
}: RouteSelectorProps) => {
    const popupRef = useRef<{ openPopup: () => void; closePopup: () => void }>(null)

    const selectedCount = selectedRoutes.length
    const buttonLabel = selectedCount ? `${selectedCount} selected` : 'Routes'

    const selectRoute = (route: string) => {
        let updatedRoutes = [...selectedRoutes]
        const isSelected = selectedRoutes.some((r) => r === route)

        if (isSelected) {
            updatedRoutes = updatedRoutes.filter((r) => r !== route)
        } else {
            updatedRoutes.push(route)
        }

        onChange(updatedRoutes)
    }

    const clearRoutes = () => {
        onChange([])
    }

    const isRouteSelected = (route: string) => selectedRoutes.some((r) => r === route)

    const filteredRoutes = [
        ...new Set(availableRoutes.filter((route) => route.toLowerCase().includes(searchValue.toLowerCase()))),
    ].sort(routeSorter)

    return (
        <Popup
            triggerComponent={() => {
                const content = triggerComponent || (
                    <Button
                        rounded
                        variant="secondary"
                        size="small"
                        className={styles.trigger}
                        disabled={!availableRoutes.length}
                    >
                        {buttonLabel}
                    </Button>
                )

                return <>{content}</>
            }}
            ref={popupRef}
            attachToTrigger
            placement="bottom"
            className={styles.popup}
            trigger="click"
        >
            <div className={styles.container} data-click-outside-exclude>
                <div className={styles.scrollContainer}>
                    <div className={styles.optionsWrapper}>
                        <div className={styles.optionsActions}>
                            <p className={styles.selectedLabel}>{selectedCount} selected</p>
                            <button
                                className={clsx(styles.clear, {
                                    [styles.clearActive]: !!selectedCount,
                                })}
                                onClick={clearRoutes}
                            >
                                Clear
                            </button>
                        </div>
                        {filteredRoutes.map((route) => (
                            <Button
                                key={route}
                                size="small"
                                variant="transparent"
                                onClick={() => selectRoute(route)}
                                className={styles.item}
                            >
                                <span>{route}</span>
                                <Checkbox variant="withoutLabel" checked={isRouteSelected(route)} />
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
        </Popup>
    )
}
